import { useState, useEffect } from 'react';

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 22) return 'evening';
  return 'night';
}

const GREETINGS = {
  morning: 'Доброго утра',
  afternoon: 'Доброго дня',
  evening: 'Доброго вечера',
  night: 'Доброй ночи',
};

// Sun/moon position on the arc: 0 = left (6:00), 0.5 = top (12:00/0:00), 1 = right (18:00)
function getArcProgress() {
  const now = new Date();
  const h = now.getHours() + now.getMinutes() / 60;
  // Map 6:00→0, 18:00→1 for day arc; 18:00→0, 6:00→1 for night arc
  if (h >= 6 && h < 18) return { progress: (h - 6) / 12, isDay: true };
  const nightH = h >= 18 ? h - 18 : h + 6;
  return { progress: nightH / 12, isDay: false };
}

export function DayCycleGreeting({ name }) {
  const [time, setTime] = useState(getTimeOfDay);
  const [arc, setArc] = useState(getArcProgress);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeOfDay());
      setArc(getArcProgress());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const greeting = name
    ? `${GREETINGS[time]}, ${name}!`
    : `${GREETINGS[time]}!`;

  // Arc geometry: semicircle from left to right
  const ARC_W = 200;
  const ARC_H = 80;
  const angle = Math.PI * (1 - arc.progress); // π→0 as progress 0→1
  const cx = ARC_W / 2 - Math.cos(angle) * (ARC_W / 2 - 16);
  const cy = ARC_H - Math.sin(angle) * (ARC_H - 16);

  // Sky gradient based on time
  const skyColors = {
    morning: ['#1e1b4b', '#92400e', '#f59e0b'],
    afternoon: ['#1e3a5f', '#3b82f6', '#93c5fd'],
    evening: ['#1e1b4b', '#7c3aed', '#f97316'],
    night: ['#0c0a09', '#1c1917', '#292524'],
  };
  const colors = skyColors[time];

  return (
    <div className="flex flex-col items-center">
      {/* Arc with sun/moon */}
      <div className="relative" style={{ width: ARC_W, height: ARC_H }}>
        {/* Sky gradient background */}
        <svg width={ARC_W} height={ARC_H} viewBox={`0 0 ${ARC_W} ${ARC_H}`} className="overflow-visible">
          <defs>
            <linearGradient id="sky-grad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={colors[0]} />
              <stop offset="50%" stopColor={colors[1]} />
              <stop offset="100%" stopColor={colors[2]} />
            </linearGradient>
            <clipPath id="arc-clip">
              <path d={`M 8,${ARC_H} A ${ARC_W / 2 - 8},${ARC_H - 8} 0 0 1 ${ARC_W - 8},${ARC_H}`} />
              <rect x="0" y="0" width={ARC_W} height={ARC_H} />
            </clipPath>
          </defs>

          {/* Arc path (track) */}
          <path
            d={`M 8,${ARC_H} A ${ARC_W / 2 - 8},${ARC_H - 8} 0 0 1 ${ARC_W - 8},${ARC_H}`}
            fill="none"
            stroke="url(#sky-grad)"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.4"
          />

          {/* Horizon line */}
          <line x1="0" y1={ARC_H} x2={ARC_W} y2={ARC_H} stroke="#44403c" strokeWidth="1" opacity="0.3" />

          {/* Sun or Moon */}
          {arc.isDay ? (
            <>
              {/* Sun glow */}
              <circle cx={cx} cy={cy} r="14" fill="#f59e0b" opacity="0.15" />
              <circle cx={cx} cy={cy} r="9" fill="#f59e0b" opacity="0.3" />
              <circle cx={cx} cy={cy} r="5" fill="#fbbf24" />
            </>
          ) : (
            <>
              {/* Moon glow */}
              <circle cx={cx} cy={cy} r="12" fill="#a5b4fc" opacity="0.1" />
              <circle cx={cx} cy={cy} r="7" fill="#c4b5fd" opacity="0.25" />
              <circle cx={cx} cy={cy} r="4.5" fill="#e2e8f0" />
              {/* Moon crater shadow */}
              <circle cx={cx + 1.5} cy={cy - 1} r="4" fill="#0c0a09" opacity="0.15" />
            </>
          )}

          {/* Small stars for night */}
          {time === 'night' && (
            <>
              <circle cx="30" cy="20" r="1" fill="#d6d3d1" opacity="0.6" />
              <circle cx="70" cy="12" r="0.8" fill="#d6d3d1" opacity="0.4" />
              <circle cx="140" cy="18" r="1" fill="#d6d3d1" opacity="0.5" />
              <circle cx="170" cy="30" r="0.7" fill="#d6d3d1" opacity="0.3" />
              <circle cx="50" cy="40" r="0.6" fill="#d6d3d1" opacity="0.3" />
            </>
          )}
        </svg>
      </div>

      {/* Greeting text */}
      <h1 className="font-heading text-2xl lg:text-3xl text-stone-200 tracking-tight mt-2 text-center">
        {greeting}
      </h1>
    </div>
  );
}
